import React, { useCallback, useEffect, useRef, useState } from 'react';

// The listing popup's main photo: every photo in a strip that snaps one at a time, so they can be browsed
// without opening the full-screen gallery. Swipe on touch screens, scroll sideways on a trackpad, or use the
// arrows (and the arrow keys while it has focus). Clicking a photo opens the gallery there. Only the photos
// next to the one on show load. `position` is controlled: the collage's small photos set it too.

const ListingPhotoStrip = ({ items, position, onPositionChange, onOpen, title, swedish = true }) => {
    const trackRef = useRef(null);
    const frameRef = useRef(0);
    const scrollingToRef = useRef(null);
    const count = items.length;
    // Photos that have been near the one on show stay loaded.
    const [loadedUpTo, setLoadedUpTo] = useState(1);

    useEffect(() => {
        setLoadedUpTo((current) => Math.max(current, position + 1));
    }, [position]);

    // Follow `position` when it changes from outside (a small photo was clicked, a new listing).
    useEffect(() => {
        const track = trackRef.current;
        if (!track || !track.clientWidth) return;
        const shown = Math.round(track.scrollLeft / track.clientWidth);
        if (shown === position) return;
        scrollingToRef.current = position;
        track.scrollTo({ left: position * track.clientWidth, behavior: Math.abs(shown - position) > 1 ? 'auto' : 'smooth' });
    }, [position]);

    useEffect(() => () => window.cancelAnimationFrame(frameRef.current), []);

    const handleScroll = useCallback(() => {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = window.requestAnimationFrame(() => {
            const track = trackRef.current;
            if (!track || !track.clientWidth) return;
            const next = Math.min(count - 1, Math.max(0, Math.round(track.scrollLeft / track.clientWidth)));
            // A scroll we started ourselves reports its stops on the way; only its end counts.
            if (scrollingToRef.current != null) {
                if (next !== scrollingToRef.current) return;
                scrollingToRef.current = null;
            }
            if (next !== position) onPositionChange(next);
        });
    }, [count, onPositionChange, position]);

    const step = (direction) => {
        const target = Math.min(count - 1, Math.max(0, position + direction));
        if (target !== position) onPositionChange(target);
    };

    const onKeyDown = (event) => {
        if (event.key === 'ArrowRight') {
            event.preventDefault();
            event.stopPropagation();
            step(1);
        } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            event.stopPropagation();
            step(-1);
        }
    };

    return (
        <div className="lfv-strip" tabIndex={count > 1 ? 0 : -1} onKeyDown={onKeyDown}
            aria-roledescription={swedish ? 'bildspel' : 'carousel'} aria-label={title}>
            <div className="lfv-strip-track" ref={trackRef} onScroll={count > 1 ? handleScroll : undefined}>
                {items.map((item, slide) => (
                    <button
                        type="button"
                        className="lfv-strip-slide"
                        key={`${item.url}-${item.index}`}
                        onClick={() => onOpen(item.index)}
                        aria-label={swedish ? `Visa bild ${slide + 1} i helskärm` : `Show photo ${slide + 1} full screen`}
                        tabIndex={-1}
                    >
                        {slide <= loadedUpTo && (
                            <img
                                src={item.url}
                                alt={slide === 0 ? title : ''}
                                loading={slide === 0 ? 'eager' : 'lazy'}
                                fetchPriority={slide === 0 ? 'high' : 'auto'}
                                draggable={false}
                            />
                        )}
                    </button>
                ))}
            </div>
            {count > 1 && (
                <>
                    <button
                        type="button"
                        className="lfv-strip-arrow is-prev"
                        onClick={() => step(-1)}
                        disabled={position === 0}
                        aria-label={swedish ? 'Föregående bild' : 'Previous photo'}
                    >
                        <span aria-hidden="true">‹</span>
                    </button>
                    <button
                        type="button"
                        className="lfv-strip-arrow is-next"
                        onClick={() => step(1)}
                        disabled={position >= count - 1}
                        aria-label={swedish ? 'Nästa bild' : 'Next photo'}
                    >
                        <span aria-hidden="true">›</span>
                    </button>
                    <span className="lfv-strip-count" aria-live="polite">{position + 1} / {count}</span>
                </>
            )}
        </div>
    );
};

export default ListingPhotoStrip;
